import { getIconComponent } from '@/components/admin/icon-map';
import { cn } from '@/lib/utils';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface FeaturesSectionProps {
  content: {
    title?: string;
    subtitle?: string;
    features?: Feature[];
    columns?: 2 | 3 | 4;
  };
  variant?: string;
  layoutStyle?: string;
}

export function FeaturesSection({ content, variant = 'default', layoutStyle = 'classic' }: FeaturesSectionProps) {
  const {
    title = 'Our Features',
    subtitle,
    features = [],
    columns = 3,
  } = content;

  const isMinimal = layoutStyle === 'modern-minimal';
  const isBold = layoutStyle === 'bold-geometric';
  const isPlayful = layoutStyle === 'playful-rounded';

  const gridColumns = features.length <= 2 ? features.length : columns;

  // Variant: Grid Cards (features in cards)
  if (variant === 'grid-cards') {
    return (
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          {(title || subtitle) && (
            <div className="text-center max-w-2xl mx-auto mb-12">
              {title && (
                <h2 className="font-display font-bold text-foreground mb-4 text-3xl md:text-4xl">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-muted-foreground text-lg">{subtitle}</p>
              )}
            </div>
          )}

          <div className={cn(
            "grid gap-6",
            gridColumns === 2 && "md:grid-cols-2",
            gridColumns === 3 && "md:grid-cols-2 lg:grid-cols-3",
            gridColumns === 4 && "md:grid-cols-2 lg:grid-cols-4"
          )}>
            {features.map((feature, index) => {
              const Icon = getIconComponent(feature.icon);
              return (
                <div 
                  key={index} 
                  className="p-6 bg-card border border-border rounded-2xl hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-lg mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Variant: Centered Icons (icons centered above text)
  if (variant === 'centered-icons') {
    return (
      <section className="section-padding bg-background">
        <div className="container mx-auto px-4">
          {(title || subtitle) && (
            <div className="text-center max-w-2xl mx-auto mb-12">
              {title && (
                <h2 className="font-display font-bold text-foreground mb-4 text-3xl md:text-4xl">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-muted-foreground text-lg">{subtitle}</p>
              )}
            </div>
          )}

          <div className={cn(
            "grid gap-12 text-center",
            gridColumns === 2 && "md:grid-cols-2",
            gridColumns === 3 && "md:grid-cols-2 lg:grid-cols-3",
            gridColumns === 4 && "md:grid-cols-2 lg:grid-cols-4"
          )}>
            {features.map((feature, index) => {
              const Icon = getIconComponent(feature.icon);
              return (
                <div key={index} className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground text-lg mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Variant: Alternating Background
  if (variant === 'alt-bg') {
    return (
      <section className="section-padding bg-secondary/30">
        <div className="container mx-auto px-4">
          {(title || subtitle) && (
            <div className="text-center max-w-2xl mx-auto mb-12">
              {title && (
                <h2 className={cn(
                  "font-display font-bold text-foreground mb-4",
                  isMinimal ? "text-3xl tracking-tight" :
                  isBold ? "text-3xl uppercase tracking-wide" :
                  "text-3xl md:text-4xl"
                )}>
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="text-muted-foreground text-lg">{subtitle}</p>
              )}
            </div>
          )}

          <div className={cn(
            "grid gap-8",
            gridColumns === 2 && "md:grid-cols-2",
            gridColumns === 3 && "md:grid-cols-2 lg:grid-cols-3",
            gridColumns === 4 && "md:grid-cols-2 lg:grid-cols-4"
          )}>
            {features.map((feature, index) => {
              const Icon = getIconComponent(feature.icon);
              return (
                <div 
                  key={index} 
                  className={cn(
                    "flex gap-4",
                    isMinimal && "flex-col items-center text-center",
                    isBold && "p-6 border-2 border-foreground",
                    isPlayful && "p-6 bg-background rounded-3xl"
                  )}
                >
                  <div className={cn(
                    "flex-shrink-0 flex items-center justify-center",
                    isMinimal ? "w-16 h-16 mb-4" :
                    isBold ? "w-12 h-12 bg-primary text-primary-foreground" :
                    isPlayful ? "w-14 h-14 rounded-2xl bg-primary/10" :
                    "w-12 h-12 rounded-xl bg-primary/10"
                  )}>
                    <Icon className={cn(
                      "text-primary",
                      isMinimal ? "w-8 h-8" :
                      isBold ? "w-6 h-6 text-primary-foreground" :
                      "w-6 h-6"
                    )} />
                  </div>
                  <div>
                    <h3 className={cn(
                      "font-semibold text-foreground mb-1",
                      isBold && "uppercase tracking-wide"
                    )}>
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    );
  }

  // Default variant
  return (
    <section className="section-padding bg-background">
      <div className="container mx-auto px-4">
        {(title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-12">
            {title && (
              <h2 className={cn(
                "font-display font-bold text-foreground mb-4",
                isMinimal ? "text-3xl tracking-tight" :
                isBold ? "text-3xl uppercase tracking-wide" :
                "text-3xl md:text-4xl"
              )}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className="text-muted-foreground text-lg">{subtitle}</p>
            )}
          </div>
        )}

        <div className={cn(
          "grid gap-8",
          gridColumns === 2 && "md:grid-cols-2",
          gridColumns === 3 && "md:grid-cols-2 lg:grid-cols-3",
          gridColumns === 4 && "md:grid-cols-2 lg:grid-cols-4"
        )}>
          {features.map((feature, index) => {
            const Icon = getIconComponent(feature.icon);
            return (
              <div 
                key={index} 
                className={cn(
                  "flex gap-4",
                  isMinimal && "flex-col items-center text-center",
                  isBold && "p-6 border-2 border-foreground",
                  isPlayful && "p-6 bg-secondary/50 rounded-3xl"
                )}
              >
                <div className={cn(
                  "flex-shrink-0 flex items-center justify-center",
                  isMinimal ? "w-16 h-16 mb-4" :
                  isBold ? "w-12 h-12 bg-primary text-primary-foreground" :
                  isPlayful ? "w-14 h-14 rounded-2xl bg-primary/10" :
                  "w-12 h-12 rounded-xl bg-primary/10"
                )}>
                  <Icon className={cn(
                    "text-primary",
                    isMinimal ? "w-8 h-8" :
                    isBold ? "w-6 h-6 text-primary-foreground" :
                    "w-6 h-6"
                  )} />
                </div>
                <div>
                  <h3 className={cn(
                    "font-semibold text-foreground mb-1",
                    isBold && "uppercase tracking-wide"
                  )}>
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}